import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Slider } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useContainerSize } from '../dashboard/hooks/useContainerSize';

const TEMP_REFERENCE = {
  'HBB R': 55,
  'HBB Y': 57,
  'HBB B': 54,
  'CT R': 61,
  'CT Y': 62,
  'CT B': 61,
};
const I2 = 1200;
const LABELS = ['HBB R', 'HBB Y', 'HBB B', 'CT R', 'CT Y', 'CT B'];

function buildGaugeOptions(gaugeValue, theme) {
  return {
    series: [
      {
        type: 'gauge',
        detail: {
          show: true,
          formatter: '{value} A',
          offsetCenter: ['0%', '-25%'],
          color: theme === 'dark' ? '#FFFFFF' : '#000000',
          rich: {
            value: { fontSize: '25px', fontWeight: 400, color: '#FFFFFF', padding: [0, 0, -20, 10] },
            unit: { fontSize: 18, color: '#FFFFFF', padding: [0, 0, -20, 10] },
          },
        },
        data: [{ value: gaugeValue, name: 'I (current)' }],
        min: 800,
        max: 1600,
        startAngle: 180,
        endAngle: 0,
        center: ['50%', '50%'],
        itemStyle: { color: '#DC272D' },
        axisLine: {
          lineStyle: {
            width: 18,
            color: [
              [(gaugeValue - 800) / 800, '#0076F6'],
              [1, '#C1C1C1'],
            ],
          },
        },
        axisLabel: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        pointer: {
          show: true,
          icon: 'path://M2.9,0.7L2.9,0.7c1.4,0,2.6,1.2,2.6,2.6v115c0,1.4-1.2,2.6-2.6,2.6l0,0c-1.4,0-2.6-1.2-2.6-2.6V3.3C0.3,1.9,1.4,0.7,2.9,0.7z',
          length: '20%',
          width: 8,
          itemStyle: { color: '#DC272D', borderRadius: 8 },
          offsetCenter: [0, '-85%'],
        },
        title: {
          show: true,
          offsetCenter: [0, '-3%'],
          textStyle: { color: '#0076F6', fontSize: 20, fontWeight: 700 },
          text: 'Simulated Temperature',
        },
      },
    ],
    tooltip: {
      backgroundColor: '#005C8E',
      borderColor: '#000000',
      formatter: '{a} <br/>{b} : {c} A',
    },
  };
}

function TemperatureCells({ gaugeValue, isCalculating, theme }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridTemplateRows: 'repeat(3, minmax(0, 1fr))', gap: 1, height: '100%', minHeight: 0 }}>
      {LABELS.map((label, index) => {
        const T2 = TEMP_REFERENCE[label];
        const sim = Math.pow(gaugeValue / I2, 61 / 100) * T2;
        const labelColor = theme === 'dark' ? '#aaa' : '#555';
        const valueColor = theme === 'dark' ? '#fff' : '#000';
        const cellBorder = theme === 'dark' ? '#3a3a3a' : '#d0d0d0';
        const cellBg = theme === 'dark' ? '#242424' : '#efefef';
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'left',
              padding: 1,
              border: `1px solid ${cellBorder}`,
              borderRadius: 1,
              backgroundColor: cellBg,
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: labelColor, mb: 0.25, fontSize: { xs: 11 } }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ color: valueColor, fontWeight: 600, fontSize: { xs: 12 }, lineHeight: 1.2 }}>
              {isCalculating ? 'Calculating…' : gaugeValue === 1200 ? `${T2} °C` : `${(sim * 0.98).toFixed(1)} – ${(sim * 1.07).toFixed(1)} °C`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Tabs / component details layout — fixed proportions. */
function SimulationGaugeClassic({ theme: themeProp }) {
  const [gaugeValue, setGaugeValue] = useState(1200);
  const [isCalculating, setIsCalculating] = useState(false);
  const reduxTheme = useSelector((state) => state.themedata.theme);
  const theme = themeProp || reduxTheme;
  const gaugeOptions = useMemo(() => buildGaugeOptions(gaugeValue, theme), [gaugeValue, theme]);

  const handleSliderChange = (_event, newValue) => {
    setIsCalculating(true);
    setGaugeValue(newValue);
    setTimeout(() => setIsCalculating(false), 2000);
  };

  return (
    <div>
      <Box sx={{ position: 'relative', height: '250px', overflow: 'hidden' }}>
        <ReactECharts option={gaugeOptions} style={{ height: '400px' }} />
      </Box>
      <Box sx={{ position: 'relative', paddingLeft: 2, paddingRight: 2 }}>
        <Slider value={gaugeValue} min={800} max={1600} step={50} onChange={handleSliderChange} aria-labelledby="simulation-slider" />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'left', marginTop: 1 }}>
        <Typography sx={{ fontWeight: 'bold', marginBottom: 1, borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`, color: theme === 'dark' ? '#ddd' : '#333' }}>
          Simulated Temperature Data:
        </Typography>
      </Box>
      <Box sx={{ marginTop: 0.5, height: { xs: 240, md: 280 }, minHeight: 200 }}>
        <TemperatureCells gaugeValue={gaugeValue} isCalculating={isCalculating} theme={theme} />
      </Box>
    </div>
  );
}

/** Dashboard widget tile — fills grid cell; chart + grid resize with ResizeObserver. */
function SimulationGaugeFill({ theme: themeProp }) {
  const [gaugeValue, setGaugeValue] = useState(1200);
  const [isCalculating, setIsCalculating] = useState(false);
  const reduxTheme = useSelector((state) => state.themedata.theme);
  const theme = themeProp || reduxTheme;
  const gaugeOptions = useMemo(() => buildGaugeOptions(gaugeValue, theme), [gaugeValue, theme]);
  const { ref: gaugeRef, width: gw, height: gh } = useContainerSize(160, 100);

  const handleSliderChange = (_event, newValue) => {
    setIsCalculating(true);
    setGaugeValue(newValue);
    setTimeout(() => setIsCalculating(false), 2000);
  };

  const ch = Math.max(72, gh);
  const cw = Math.max(80, gw);

  return (
    <Box sx={{ height: '100%', width: '100%', minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      <Box ref={gaugeRef} sx={{ flex: '5 5 52%', minHeight: 72, overflow: 'hidden', position: 'relative' }}>
        {gw > 0 && gh > 0 ? <ReactECharts option={gaugeOptions} style={{ width: cw, height: ch }} /> : null}
      </Box>
      <Box sx={{ flexShrink: 0, px: 1.5 }}>
        <Slider value={gaugeValue} min={800} max={1600} step={50} onChange={handleSliderChange} aria-labelledby="simulation-slider-dash" size="small" />
      </Box>
      <Typography sx={{ flexShrink: 0, mt: 0.5, fontWeight: 'bold', fontSize: 12, borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`, color: theme === 'dark' ? '#ddd' : '#333', px: 0.25 }}>
        Simulated Temperature Data:
      </Typography>
      <Box sx={{ flex: '4 4 43%', minHeight: 96, mt: 0.5, overflow: 'hidden' }}>
        <TemperatureCells gaugeValue={gaugeValue} isCalculating={isCalculating} theme={theme} />
      </Box>
    </Box>
  );
}

function SimulationGauge({ theme: themeProp, fillContainer = false }) {
  if (fillContainer) return <SimulationGaugeFill theme={themeProp} />;
  return <SimulationGaugeClassic theme={themeProp} />;
}

export default React.memo(SimulationGauge);
